import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from 'tailwind-v4/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'tailwind-v4/components/dialog'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from 'tailwind-v4/components/field'
import { PinPad, UserPinLoginDialog } from 'tailwind-v4/blocks/pin-pad'
import { Spinner } from 'tailwind-v4/components/spinner'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import { isAxiosError } from '@/utils/axiosHelper'
import { gravatarImage } from '@/utils/common'
import { readPinV2Error } from '@/utils/pin-login'

const PIN_LENGTH = 4

/**
 * A 4xx from the API carries a `{ message }` body worth showing verbatim
 * ("PIN already in use", "Too many attempts"); anything else gets the caller's
 * fallback.
 */
function readApiMessage(error: unknown, fallback: string): string {
  if (
    isAxiosError(error) &&
    error.response?.status &&
    error.response.status >= 400 &&
    error.response.status < 500
  ) {
    const parsed = z
      .object({ message: z.string() })
      .safeParse(error.response.data)
    if (parsed.success) {
      return parsed.data.message
    }
  }
  return fallback
}

/**
 * PIN login: pick yourself from the org's user list, then enter your PIN.
 * Not a route: it owns its own open state and is rendered from the account
 * menu.
 */
function PinAuth({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: () => void
}) {
  const { org_id: orgId = '' } = useParams<{ org_id: string }>()
  const [error, setError] = useState<string | null>(null)
  const { pathname } = useLocation()

  const { data: users = [], isFetching: isLoadingUsers } =
    morpheusApi.useGetOrganisationUsersQuery({ orgId }, { skip: !orgId })

  const [authWithPin, { isLoading }] =
    morpheusApi.useGetAuthCodeUsingOrganisationPinV2Mutation()
  const [generateToken, { isLoading: isGeneratingToken }] =
    morpheusApi.useLazyGetAuthWithCodeQuery()

  const isChecking = isLoading || isGeneratingToken

  const handlePinAuthentication = async (oauthUserId: string, pin: string) => {
    if (!orgId) {
      setError('Invalid organisation')
      return
    }

    setError(null)

    let responseRedirectUrl: string
    try {
      responseRedirectUrl = await authWithPin({
        orgId,
        oauthUserId,
        pin,
        state: pathname,
      }).unwrap()
    } catch (err: unknown) {
      setError(readPinV2Error(err))
      return
    }

    const url = new URL(responseRedirectUrl)
    const code = url.searchParams.get('code')

    if (!code) {
      setError('Authentication failed: Missing authorisation code')
      return
    }

    try {
      await generateToken({ code }).unwrap()
      onComplete()
      window.location.reload()
    } catch {
      setError('Failed to generate access token')
    }
  }

  return (
    <UserPinLoginDialog
      open={open}
      onOpenChange={onOpenChange}
      users={users.map((u) => ({
        id: u.user_id,
        first: u.oauth_users.first,
        last: u.oauth_users.last,
        email: u.oauth_users.email,
      }))}
      isLoadingUsers={isLoadingUsers}
      onComplete={(oauthUserId, pin) => {
        void handlePinAuthentication(oauthUserId, pin)
      }}
      error={error}
      isChecking={isChecking}
      length={PIN_LENGTH}
      resolveAvatarUrl={gravatarImage}
    />
  )
}

/**
 * Create, replace or remove a team member's PIN. Rendered from the profile
 * page, so it is the dialog itself rather than a page.
 */
export function SetPinAuth({
  user_id,
  open,
  onOpenChange,
}: {
  user_id: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { org_id: orgId = '' } = useParams<{ org_id: string }>()

  const { data: userPin = null } = morpheusApi.useGetOrganisationUserPinQuery(
    {
      orgId,
      uid: user_id,
    },
    {
      skip: !orgId || !user_id,
    }
  )

  const [updateUserPin, { isLoading: isUpdatingPin }] =
    morpheusApi.useUpdateOrganisationUserPinMutation()

  const [deleteUserPin, { isLoading: isDeletingPin }] =
    morpheusApi.useDeleteOrganisationUserPinMutation()

  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)

  const hasPin = userPin?.has_pin ?? false
  const saveLabel = hasPin ? 'Update PIN' : 'Create PIN'
  const pinHint = hasPin
    ? 'Entering a new PIN replaces the current one.'
    : 'Four digits, used in place of a password when switching accounts.'

  const savePin = async () => {
    setError(null)
    try {
      await updateUserPin({
        orgId,
        uid: user_id,
        pin,
      }).unwrap()
      toast.success(hasPin ? 'PIN updated' : 'PIN created')
    } catch (err: unknown) {
      setError(readApiMessage(err, 'Error updating pin'))
    }
  }

  const removePin = async () => {
    setError(null)
    try {
      await deleteUserPin({
        orgId,
        uid: user_id,
      }).unwrap()
      toast.success('PIN deleted')
    } catch (err: unknown) {
      setError(readApiMessage(err, 'Error deleting pin'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>PIN access</DialogTitle>
          <DialogDescription>
            A PIN lets someone sign in without their password, so it bypasses
            two-factor authentication and makes the account less secure. We only
            recommend it for front-of-house staff who need to switch between
            users quickly.
          </DialogDescription>
        </DialogHeader>

        <Field>
          <FieldLabel htmlFor="pin">PIN</FieldLabel>
          <PinPad
            id="pin"
            value={pin}
            onValueChange={(value: string) => {
              setPin(value)
              setError(null)
            }}
            disabled={isUpdatingPin || isDeletingPin}
            length={PIN_LENGTH}
          />
          <FieldDescription>{pinHint}</FieldDescription>
          <FieldError>{error}</FieldError>
        </Field>

        <DialogFooter>
          {hasPin ? (
            <Button
              variant="ghost"
              leftIcon={<Trash2 />}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              disabled={isDeletingPin}
              onClick={() => {
                void removePin()
              }}
            >
              Remove PIN
            </Button>
          ) : null}
          <Button
            variant="primary"
            disabled={pin.length !== PIN_LENGTH || isUpdatingPin}
            leftIcon={isUpdatingPin ? <Spinner /> : undefined}
            onClick={() => {
              void savePin()
            }}
          >
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PinAuth
